<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import ProjectDialog from '@/components/projects/ProjectDialog.vue';
import DuplicateBomDialog from '@/components/projects/DuplicateBomDialog.vue';
import BomCompareDialog from '@/components/projects/BomCompareDialog.vue';
import ProjectBomsList from '@/components/projects/ProjectBomsList.vue';
import { useAlertsStore } from '@/store/AlertsStore';
import { useBomStore } from '@/store/BomStore';
import type {
  CatalogueProject,
  CatalogueProjectBom,
  CatalogueProjectBomCreate,
  CatalogueProjectBomUpdate,
} from '@/models/Projects';
import {
  CatalogueProjectBomDuplicateType,
  CatalogueProjectBomType,
} from '@/models/Projects';
import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
  getProjectBoms,
  createProjectBom,
  updateProjectBom,
  deleteProjectBom,
  duplicateProjectBom,
} from '@/api/ProjectsApi';
import { requiredRule } from '@/helpers/FormRules';

const { $gettext } = gettext;
const alerts = useAlertsStore();
const bomStore = useBomStore();

const emit = defineEmits<{
  (e: 'select-bom', project: CatalogueProject, bom: CatalogueProjectBom): void;
  (e: 'close'): void;
}>();

const projects = ref<CatalogueProject[]>([]);
const searchQuery = ref('');
const filteredProjects = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) {
    return projects.value;
  }
  return projects.value.filter(
    (p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.dealId || '').toLowerCase().includes(q),
  );
});
const loadingProjects = ref(false);
const bomsByProject = ref<Record<string, CatalogueProjectBom[]>>({});

// Project Dialog state
const showProjectDialog = ref(false);
const editingProject = ref<CatalogueProject | null>(null);
const projectForm = ref<{ name: string; dealId: string | null }>({
  name: '',
  dealId: '',
});

// Project Delete
const showProjectDeleteDialog = ref(false);
const projectToDelete = ref<CatalogueProject | null>(null);

// BOM Dialog state
const showBomDialog = ref(false);
const editingBom = ref<CatalogueProjectBom | null>(null);
const activeBomProjectUuid = ref<string | null>(null);
const bomForm = ref<{ name: string; bomType: CatalogueProjectBomType }>({
  name: '',
  bomType: CatalogueProjectBomType.ebom,
});
const bomFormValid = ref(false);

// BOM Delete
const showBomDeleteDialog = ref(false);
const bomToDelete = ref<CatalogueProjectBom | null>(null);

// Duplicate BOM
const showBomDuplicateDialog = ref(false);
const bomToDuplicate = ref<CatalogueProjectBom | null>(null);
const selectedDuplicateTargetProjectUuid = ref<string | null>(null);
const selectedDuplicateBranchType = ref<CatalogueProjectBomDuplicateType>(
  CatalogueProjectBomDuplicateType.default,
);

// Compare BOMs
const showBomCompareDialog = ref(false);
const compareProjectUuid = ref<string | null>(null);
const bomsForCompare = computed(() =>
  compareProjectUuid.value
    ? bomsByProject.value[compareProjectUuid.value] || []
    : [],
);
const projectNameForCompare = computed(() => {
  if (!compareProjectUuid.value) {
    return '';
  }
  const p = projects.value.find((pr) => pr.uuid === compareProjectUuid.value);
  return p?.name || '';
});

const loadProjects = async () => {
  loadingProjects.value = true;
  try {
    projects.value = await getProjects();
  } finally {
    loadingProjects.value = false;
  }
};

const refreshProjectBoms = async (projectUuid: string) => {
  try {
    bomsByProject.value[projectUuid] = await getProjectBoms(projectUuid);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error(e);
    alerts.error($gettext('Failed to load BOMs'));
  }
};

const expandedIndex = ref<number | undefined>(undefined);
const onExpandedChange = async (val: unknown) => {
  const idx = Array.isArray(val)
    ? (val[0] as number | undefined)
    : (val as number | undefined);
  if (idx === undefined) {
    return;
  }
  const project = filteredProjects.value[idx];
  if (project) {
    await refreshProjectBoms(project.uuid);
  }
};

// Project actions
const openCreateProject = () => {
  editingProject.value = null;
  projectForm.value = { name: '', dealId: '' };
  showProjectDialog.value = true;
};

const openEditProject = (project: CatalogueProject) => {
  editingProject.value = project;
  projectForm.value = { name: project.name, dealId: project.dealId || '' };
  showProjectDialog.value = true;
};

const handleSaveProject = async (payload: {
  name: string;
  dealId: string | null;
  uuid?: string;
}) => {
  try {
    if (payload.uuid) {
      await updateProject(payload.uuid, {
        name: payload.name,
        dealId: payload.dealId,
      });
      if (bomStore.selectedProject?.uuid === payload.uuid) {
        bomStore.selectedProject = {
          ...bomStore.selectedProject,
          name: payload.name,
        };
      }
      alerts.success($gettext('Project updated'));
    } else {
      await createProject({ name: payload.name, dealId: payload.dealId });
      alerts.success($gettext('Project created'));
    }
    await loadProjects();
    showProjectDialog.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if (e.response?.status === 409) {
      alerts.error(
        $gettext('Project with this name or deal ID already exists'),
      );
    } else if (e.response?.status === 404) {
      alerts.error($gettext('This deal ID was not found in HubSpot'));
    } else if (e.response?.status === 502) {
      alerts.error(
        $gettext(
          'Could not reach HubSpot to validate deal ID. Please try again later or remove the deal ID',
        ),
      );
    } else {
      alerts.error($gettext('Failed to save project'));
    }
  }
};

const confirmDeleteProject = (project: CatalogueProject) => {
  projectToDelete.value = project;
  showProjectDeleteDialog.value = true;
};

const deleteProjectAction = async () => {
  if (!projectToDelete.value) {
    return;
  }
  try {
    await deleteProject(projectToDelete.value.uuid);
    const { uuid } = projectToDelete.value;
    delete bomsByProject.value[uuid];
    await loadProjects();
    alerts.success($gettext('Project deleted'));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if (e.response?.status === 409) {
      alerts.error(
        $gettext('Failed to delete project. Project still contains BOMs'),
      );
    } else {
      alerts.error($gettext('Failed to delete project'));
    }
  } finally {
    showProjectDeleteDialog.value = false;
    projectToDelete.value = null;
  }
};

// BOM actions
const openCreateBom = (projectUuid: string) => {
  editingBom.value = null;
  activeBomProjectUuid.value = projectUuid;
  bomForm.value = { name: '', bomType: CatalogueProjectBomType.ebom };
  showBomDialog.value = true;
};

const openEditBom = (bom: CatalogueProjectBom) => {
  editingBom.value = bom;
  activeBomProjectUuid.value = bom.projectUuid;
  bomForm.value = { name: bom.name, bomType: bom.bomType };
  showBomDialog.value = true;
};

const saveBom = async () => {
  if (!activeBomProjectUuid.value || !bomFormValid.value) {
    return;
  }
  const form = bomForm.value;
  try {
    if (editingBom.value) {
      const payload: CatalogueProjectBomUpdate = {
        name: form.name,
        bomType: form.bomType,
      };
      const updated = await updateProjectBom(editingBom.value.uuid, payload);
      // mutate local list
      const list = bomsByProject.value[activeBomProjectUuid.value] || [];
      const idx = list.findIndex((b) => b.uuid === editingBom.value?.uuid);
      if (idx !== -1) {
        list[idx] = {
          ...list[idx],
          name: updated.name,
          bomType: updated.bomType,
        } as CatalogueProjectBom;
        bomsByProject.value[activeBomProjectUuid.value] = [...list];
      }
      if (bomStore.selectedBom?.uuid === editingBom.value.uuid) {
        bomStore.selectedBom = {
          ...bomStore.selectedBom,
          name: updated.name,
          bomType: updated.bomType,
        };
      }
      alerts.success($gettext('BOM updated'));
    } else {
      const payload: CatalogueProjectBomCreate = {
        name: form.name,
        bomType: form.bomType,
        projectUuid: activeBomProjectUuid.value,
      };
      const created = await createProjectBom(payload);
      // append to local list if present
      const list = bomsByProject.value[activeBomProjectUuid.value] || [];
      bomsByProject.value[activeBomProjectUuid.value] = [...list, created];
      alerts.success($gettext('BOM created'));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error(e);
    alerts.error($gettext('Failed to save BOM'));
  } finally {
    showBomDialog.value = false;
  }
};

const confirmDeleteBom = (bom: CatalogueProjectBom) => {
  bomToDelete.value = bom;
  showBomDeleteDialog.value = true;
};

const deleteBomAction = async () => {
  if (!bomToDelete.value) {
    return;
  }
  try {
    await deleteProjectBom(bomToDelete.value.uuid);
    const { projectUuid } = bomToDelete.value;
    const list = bomsByProject.value[projectUuid] || [];
    bomsByProject.value[projectUuid] = list.filter(
      (b) => b.uuid !== bomToDelete.value?.uuid,
    );
    alerts.success($gettext('BOM deleted'));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if (e.response?.status === 400) {
      alerts.error(
        $gettext('Cannot delete BOM that has branches or version descendants'),
      );
    } else {
      alerts.error($gettext('Failed to delete BOM'));
    }
  } finally {
    showBomDeleteDialog.value = false;
    bomToDelete.value = null;
  }
};

const openDuplicateBom = (bom: CatalogueProjectBom) => {
  bomToDuplicate.value = bom;
  selectedDuplicateTargetProjectUuid.value = null;
  showBomDuplicateDialog.value = true;
};

const confirmDuplicateBom = async () => {
  if (!bomToDuplicate.value || !selectedDuplicateTargetProjectUuid.value) {
    return;
  }
  try {
    const duplicated = await duplicateProjectBom({
      targetProjectUuid: selectedDuplicateTargetProjectUuid.value,
      bomUuid: bomToDuplicate.value.uuid,
      branchType: selectedDuplicateBranchType.value,
    });
    // add to target project's list if already loaded
    const targetUuid = selectedDuplicateTargetProjectUuid.value;
    const list = bomsByProject.value[targetUuid];
    if (Array.isArray(list)) {
      bomsByProject.value[targetUuid] = [...list, duplicated];
    }
    if (
      selectedDuplicateBranchType.value ===
      CatalogueProjectBomDuplicateType.branch
    ) {
      alerts.success($gettext('BOM branched'));
    } else if (
      selectedDuplicateBranchType.value ===
      CatalogueProjectBomDuplicateType.version
    ) {
      alerts.success($gettext('BOM version created'));
    } else {
      alerts.success($gettext('BOM duplicated'));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error(e);
    if (e.response?.status === 409) {
      alerts.error(
        $gettext('Cannot create a new version: next version already exists'),
      );
    } else if (e.response?.status === 400) {
      alerts.error(
        $gettext('Cannot branch or version to a different target project'),
      );
    } else {
      alerts.error($gettext('Failed to duplicate BOM'));
    }
  } finally {
    showBomDuplicateDialog.value = false;
    bomToDuplicate.value = null;
    selectedDuplicateTargetProjectUuid.value = null;
    selectedDuplicateBranchType.value =
      CatalogueProjectBomDuplicateType.default;
  }
};

const openBomCompareDialog = async (projectUuid: string) => {
  compareProjectUuid.value = projectUuid;
  await refreshProjectBoms(projectUuid);
  showBomCompareDialog.value = true;
};

onMounted(() => {
  void loadProjects();
});
</script>

<template>
  <v-card variant="outlined" class="projects-sidebar-card">
    <v-card-title class="d-flex align-center">
      <v-icon icon="mdi-folder-outline" class="my-4" />
      {{ $gettext('Projects & BOMs') }}
      <v-spacer />
      <v-text-field
        v-model="searchQuery"
        variant="outlined"
        density="compact"
        hide-details
        prepend-inner-icon="mdi-magnify"
        :label="$gettext('Search')"
        class="mr-2"
        style="max-width: 240px"
      />
      <r-btn
        variant="outlined"
        icon="mdi-plus"
        size="small"
        @click="openCreateProject"
      >
        {{ $gettext('Create Project') }}
      </r-btn>
    </v-card-title>
    <v-divider />
    <v-card-text class="mt-2 projects-sidebar-scroll">
      <div
        v-if="filteredProjects.length === 0"
        class="text-medium-emphasis text-h6 text-center font-italic"
      >
        {{ $gettext('No projects found') }}
      </div>
      <v-expansion-panels
        v-else
        v-model="expandedIndex"
        variant="accordion"
        @update:modelValue="onExpandedChange"
      >
        <v-expansion-panel
          v-for="(project, idx) in filteredProjects"
          :key="project.uuid"
        >
          <v-expansion-panel-title>
            <div class="d-flex align-center flex-grow-1">
              <div class="text-subtitle-1">{{ project.name }}</div>
              <div v-if="project.dealId" class="text-medium-emphasis ml-2">
                — {{ project.dealId }}
              </div>
              <v-btn
                v-if="project.hubspotLink"
                :href="project.hubspotLink"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="text"
                :title="$gettext('View in HubSpot')"
                @click.stop
              >
                {{ $gettext('View in HubSpot') }}
                <v-icon end size="x-small" icon="mdi-open-in-new" />
              </v-btn>
              <v-spacer />
              <v-btn
                icon="mdi-compare"
                size="small"
                variant="text"
                :title="$gettext('Compare BOMs')"
                @click.stop="openBomCompareDialog(project.uuid)"
              />
              <v-btn
                v-if="project.canEdit"
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click.stop="openEditProject(project)"
              />
              <v-btn
                v-if="project.canEdit"
                icon="mdi-delete"
                size="small"
                color="error"
                variant="text"
                @click.stop="confirmDeleteProject(project)"
              />
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <ProjectBomsList
              :project="project"
              :boms="bomsByProject[project.uuid] || []"
              :selected-bom-uuid="bomStore.selectedBom?.uuid || null"
              @select-bom="(bom) => emit('select-bom', project, bom)"
              @create-bom="openCreateBom(project.uuid)"
              @edit-bom="openEditBom"
              @duplicate-bom="openDuplicateBom"
              @delete-bom="confirmDeleteBom"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
    <v-divider />
    <v-card-actions class="justify-end">
      <r-btn variant="outlined" icon="mdi-close" @click="emit('close')">
        {{ $gettext('Close') }}
      </r-btn>
    </v-card-actions>
  </v-card>

  <!-- Project Dialog -->
  <ProjectDialog
    v-model="showProjectDialog"
    :project="editingProject || undefined"
    @save="handleSaveProject"
  />

  <!-- Project Delete Dialog -->
  <v-dialog v-model="showProjectDeleteDialog" max-width="420">
    <v-card>
      <v-card-title>{{ $gettext('Delete Project') }}</v-card-title>
      <v-card-text>{{
        $gettext('Are you sure you want to delete this project?')
      }}</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showProjectDeleteDialog = false">{{
          $gettext('Cancel')
        }}</v-btn>
        <v-btn color="error" @click="deleteProjectAction">{{
          $gettext('Delete')
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- BOM Dialog -->
  <v-dialog v-model="showBomDialog" max-width="480">
    <v-card>
      <v-card-title>{{
        editingBom ? $gettext('Edit BOM') : $gettext('Create BOM')
      }}</v-card-title>
      <v-card-text>
        <v-form v-model="bomFormValid">
          <v-text-field
            v-model="bomForm.name"
            :label="$gettext('Name')"
            :rules="[requiredRule]"
          />
          <v-select
            v-model="bomForm.bomType"
            :label="$gettext('BOM Type')"
            :items="[
              CatalogueProjectBomType.ebom,
              CatalogueProjectBomType.dbom,
            ]"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showBomDialog = false">{{
          $gettext('Cancel')
        }}</v-btn>
        <v-btn color="primary" :disabled="!bomFormValid" @click="saveBom">{{
          $gettext('Save')
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- BOM Delete Dialog -->
  <v-dialog v-model="showBomDeleteDialog" max-width="420">
    <v-card>
      <v-card-title>{{ $gettext('Delete BOM') }}</v-card-title>
      <v-card-text>{{
        $gettext('Are you sure you want to delete this BOM?')
      }}</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showBomDeleteDialog = false">{{
          $gettext('Cancel')
        }}</v-btn>
        <v-btn color="error" @click="deleteBomAction">{{
          $gettext('Delete')
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- BOM Duplicate Dialog -->
  <DuplicateBomDialog
    v-model="showBomDuplicateDialog"
    :title="$gettext('Duplicate BOM to Project')"
    @confirm="
      (
        targetUuid: string | null,
        branchType: CatalogueProjectBomDuplicateType,
      ) => {
        selectedDuplicateTargetProjectUuid =
          targetUuid ?? bomToDuplicate?.projectUuid ?? null;
        selectedDuplicateBranchType = branchType;
        confirmDuplicateBom();
      }
    "
  />

  <!-- BOM Compare Dialog -->
  <BomCompareDialog
    v-model="showBomCompareDialog"
    :project-name="projectNameForCompare"
    :boms="bomsForCompare"
  />
</template>

<style scoped>
.projects-sidebar-card {
  height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
}
.projects-sidebar-scroll {
  overflow-y: auto;
  flex: 1 1 auto;
}
</style>
